import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Scissors } from 'lucide-react';

function SplitTaskDialog({ open, onClose, schedule, onConfirm }) {
  const [splitQuantity, setSplitQuantity] = useState('');
  const [error, setError] = useState('');

  const maxQty = schedule?.assigned_quantity || 0;

  useEffect(() => {
    if (open) {
      setSplitQuantity('');
      setError('');
    }
  }, [open]);

  const qty = useMemo(() => {
    const parsed = parseInt(splitQuantity, 10);
    return isNaN(parsed) ? 0 : parsed;
  }, [splitQuantity]);

  const remainingQty = useMemo(() => Math.max(0, maxQty - qty), [maxQty, qty]);
  const ratio = useMemo(() => (maxQty > 0 ? Math.round((qty / maxQty) * 100) : 0), [qty, maxQty]);

  const presets = useMemo(() => {
    if (maxQty <= 1) return [];
    return [
      { label: '25%', value: Math.max(1, Math.floor(maxQty * 0.25)) },
      { label: '50%', value: Math.max(1, Math.floor(maxQty * 0.5)) },
      { label: '75%', value: Math.max(1, Math.floor(maxQty * 0.75)) },
      { label: '全部剩余', value: maxQty - 1 },
    ];
  }, [maxQty]);

  const handleConfirm = () => {
    if (qty < 1 || qty >= maxQty) {
      setError(`拆分数量必须在 1 到 ${maxQty - 1} 之间`);
      return;
    }
    onConfirm(qty);
  };

  const handleSliderChange = (e) => {
    setSplitQuantity(e.target.value);
    setError('');
  };

  const isValid = qty >= 1 && qty < maxQty;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="w-[calc(100%-2rem)] sm:max-w-[420px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="h-4 w-4" />
            拆分排程任务
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {schedule && (
            <div className="text-sm text-gray-600 space-y-1 bg-gray-50 p-3 rounded-md">
              <div><span className="font-medium">工序:</span> {schedule.step_name}</div>
              <div><span className="font-medium">产品:</span> {schedule.finished_product_code}</div>
              <div><span className="font-medium">当前分配数量:</span> {schedule.assigned_quantity} 件</div>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="split-qty">拆分数量（新任务）</Label>
              <span className="text-xs text-gray-500">{ratio}% · 共 {maxQty} 件</span>
            </div>

            <Input
              id="split-qty"
              type="number"
              min={1}
              max={maxQty - 1}
              value={splitQuantity}
              onChange={(e) => {
                setSplitQuantity(e.target.value);
                setError('');
              }}
              placeholder={`输入 1 ~ ${maxQty - 1}`}
              className="text-base"
            />

            {maxQty > 2 && (
              <div className="space-y-1.5">
                <input
                  type="range"
                  min={1}
                  max={maxQty - 1}
                  value={qty || 1}
                  onChange={handleSliderChange}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-[10px] text-gray-400 px-0.5">
                  <span>1</span>
                  <span>{maxQty - 1}</span>
                </div>
              </div>
            )}

            {presets.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {presets.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => {
                      setSplitQuantity(String(p.value));
                      setError('');
                    }}
                    className="px-2 py-1 text-xs rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-blue-50 hover:border-blue-200 transition-colors"
                  >
                    {p.label} · {p.value}件
                  </button>
                ))}
              </div>
            )}

            {qty > 0 && (
              <div className="text-sm bg-blue-50 text-blue-800 p-3 rounded-md space-y-1">
                <div className="flex items-center justify-between">
                  <span>原任务保留</span>
                  <span className="font-semibold">{remainingQty} 件</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>新任务</span>
                  <span className="font-semibold">{qty} 件</span>
                </div>
                <div className="h-px bg-blue-200 my-1" />
                <div className="text-xs text-blue-600">
                  拆分后原任务数量变为 {remainingQty}，同时生成一个 {qty} 件的新任务。
                </div>
              </div>
            )}

            {error && <div className="text-xs text-red-500">{error}</div>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button onClick={handleConfirm} disabled={!isValid}>
            确认拆分
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SplitTaskDialog;
